import React from 'react';

import { GetClientItem, useGetClient } from '../data/useGetClient';
import { GetJobResult, useGetJob } from '../data/useGetJob';
import {
  GetMemberListItem,
  useGetMembersList,
} from '../data/useGetMembersList';
import { SearchTaskItem, useSearchTasks } from '../data/useSearchTasks';
import {
  GetJobStatusListResult,
  useGetJobStatusList,
} from '../data/useGetJobStatusList';

export interface Job extends Omit<GetJobResult, 'tasks'> {
  tasks?: SearchTaskItem[];
  client?: GetClientItem;
  partner?: GetMemberListItem;
  manager?: GetMemberListItem;
  staff?: GetMemberListItem;
  assignee?: GetMemberListItem;
  status: GetJobStatusListResult;
}

const emptyReturn = { loading: false, error: false, data: undefined };

export const useQueryJob = (options: {
  variables: { cloudId: string };
  skip?: boolean;
}) => {
  const statuses = useGetJobStatusList({ skip: options.skip });
  const members = useGetMembersList({ skip: options.skip });
  const job = useGetJob({ variables: options.variables, skip: options.skip });

  const client = useGetClient(
    !options.skip && job.data?.clientId
      ? {
          variables: { cloudId: job.data.clientId },
        }
      : { skip: true }
  );

  const tasks = useSearchTasks({
    variables: { myWork: false, jobs: [options.variables.cloudId] },
    skip: options.skip,
  });

  return React.useMemo(() => {
    if (options.skip) {
      // not entirely sure what to return here. What's a normal query return when it is skipped?
      return emptyReturn;
    }
    if (
      client.loading ||
      job.loading ||
      members.loading ||
      tasks.loading ||
      statuses.loading
    )
      return { loading: true };
    if (
      client.error ||
      job.error ||
      members.error ||
      tasks.error ||
      statuses.error
    )
      return { error: true };

    const clientData = client.data;
    const jobData = job.data;
    const membersData = members.data;
    const tasksData = tasks.data;

    const status = statuses.data?.find(
      (s) => s.cloudId === jobData?.jobStatusId
    );
    if (jobData && !status) {
      console.error(
        `Query Jobs Without Tasks: unable to find status "${jobData.jobStatusId}" for job "${jobData.cloudId}"`
      );
    }

    if (
      (job.data?.clientId && !clientData) ||
      !jobData ||
      !membersData ||
      !tasksData ||
      !status
    ) {
      return { error: true };
    }

    const jobTasks = tasksData.filter((task) => task.jobId === jobData.cloudId);
    const partner = membersData.find(
      (member) => member.cloudId === jobData.partnerUserId
    );
    const manager = membersData.find(
      (member) => member.cloudId === jobData.managerUserId
    );
    const staff = membersData.find(
      (member) => member.cloudId === jobData.staffUserId
    );
    const assignee = membersData.find(
      (member) => member.cloudId === jobData.assigneeUserId
    );

    return {
      data: {
        ...jobData,
        tasks: jobTasks,
        client: clientData ?? undefined,
        partner,
        manager,
        staff,
        assignee,
        status,
      } as Job,
    };
  }, [client, job, members, tasks, options, statuses]);
};
